export const financeSessionItems = [
	{
		type: "SET",
		stimulus: {
			id: 50198,
			content: {
				instruction:
					"Watch the video and select the correct answer from the options provided.",
				passage: {
					title: "Finance Case: Financial Statement Analysis",
					paragraphs: [
						{
							text: "A financial scenario is presented. You see a description of a 35-year-old business owner contacting a telefinance service with concerns about irregularities in his financial statements, discrepancies between cash flow and bank records, and potential fraudulent activities.",
							number: 1,
						},
					],
				},
				media: [
					{
						id: "financeVid001",
						url: "vi6VL8q7XMZm3lJt6ZZ4e00o",
						type: "video",
						transcript:
							"Hi, I'm calling because I've noticed some inconsistencies in my financial statements. In the last quarter, my cash flow appears to have significant discrepancies with the bank records, and there are unusual charges that I cannot account for. I'm concerned that these issues could indicate either a clerical error or potential fraud. Could you help me understand what might be causing these discrepancies and advise on the next steps?",
					},
				],
			},
			metadata: {
				type: "READING",
				skill: "READING",
				title: "Reading Stimulus",
			},
		},
		items: [
			{
				id: 50199,
				type: "MCSS",
				content: {
					parentId: 50198,
					parentType: "SET",
					stem: "Based on the business owner's description, which initial financial analysis method would you use to investigate the discrepancies in the statements?",
					choices: [
						{
							id: "fin_choice1",
							stem: "Bank reconciliation analysis",
							text: "Bank reconciliation analysis",
							isCorrect: true,
							explanation: `
A **bank reconciliation analysis** is the most appropriate initial financial method for this scenario. **Bank reconciliation is the standard process** for ensuring that a company’s internal cash records match the bank statements, thereby detecting discrepancies and potential errors. Given the business owner's concerns—**irregularities in financial statements, mismatches between cash flow and bank records, and unusual transactions**—this analysis is the best choice to quickly identify timing differences, clerical errors, or even fraudulent activities. It is widely recognized, cost-effective, and provides **a systematic approach to verify the accuracy of financial records** with minimal delay.

While other financial analysis techniques exist, they are **not the preferred initial method** in this context. **Investment risk assessments** focus on market performance rather than internal accounting accuracy, **market trend analysis** examines external economic conditions, and **credit scoring** is primarily used for evaluating creditworthiness. None of these methods directly address the need to reconcile internal records with bank data as effectively as bank reconciliation analysis.

For **an immediate and thorough investigation of financial discrepancies**, **bank reconciliation analysis remains the most effective and efficient method.**

**Key Takeaways:**
- **Bank reconciliation analysis ensures that internal cash records align with bank statements**, providing a clear audit trail.
- **It is the first-line method to detect discrepancies, errors, or potential fraud in financial statements.**
- **Other methods are valuable for broader financial assessments** but do not specifically resolve internal record mismatches.

**References:**
- Investopedia. (2023). Bank reconciliation. *Investopedia*. Retrieved from [https://www.investopedia.com/terms/b/bank-reconciliation.asp](https://www.investopedia.com/terms/b/bank-reconciliation.asp)
- American Institute of CPAs (AICPA). (2022). *Understanding bank reconciliations*. Retrieved from [https://www.aicpa.org/](https://www.aicpa.org/)
- Financial Accounting Standards Board (FASB). (2021). *Financial reporting and reconciliation standards*. Retrieved from [https://www.fasb.org/](https://www.fasb.org/)
							`,
						},
						{
							id: "fin_choice2",
							stem: "Investment risk assessment",
							text: "Investment risk assessment",
							isCorrect: false,
						},
						{
							id: "fin_choice3",
							stem: "Market trend analysis",
							text: "Market trend analysis",
							isCorrect: false,
						},
						{
							id: "fin_choice4",
							stem: "Credit scoring",
							text: "Credit scoring",
							isCorrect: false,
						},
					],
					instruction: "Choose the best answer",
					highlights: null,
					media: null,
				},
				tags: [
					{
						id: 6479,
						type: "Skill",
						label: "Reading",
						breadcrumb: "Reading",
					},
				],
				metadata: {
					type: "MCSS",
					skill: "READING",
					title: "Multiple Choice Single Selection",
					topics: ["Financial Analysis"],
				},
				response: {
					maximumScore: 1,
					correctResponse: [
						{
							id: "fin_choice1",
						},
					],
					numChoicesRequired: {
						max: 1,
						min: 1,
					},
				},
			},
		],
	},
	{
		type: "SET",
		stimulus: {
			id: 501998,
			content: {
				instruction:
					"Watch the video and write your answer in the text box provided.",
				passage: {
					title: "Finance Case: Financial Statement Analysis",
					paragraphs: [
						{
							text: "A financial scenario is presented. You see a description of a 35-year-old business owner contacting a telefinance service with concerns about irregularities in his financial statements, discrepancies between cash flow and bank records, and potential fraudulent activities.",
							number: 1,
						},
					],
				},
				media: [
					{
						id: "financeVid001",
						url: "vi6VL8q7XMZm3lJt6ZZ4e00o",
						type: "video",
						transcript:
							"Hi, I'm calling because I've noticed some inconsistencies in my financial statements. In the last quarter, my cash flow appears to have significant discrepancies with the bank records, and there are unusual charges that I cannot account for. I'm concerned that these issues could indicate either a clerical error or potential fraud. Could you help me understand what might be causing these discrepancies and advise on the next steps?",
					},
				],
			},
			metadata: {
				type: "INTEGRATED_WRITING_RESPONSE",
				skill: "WRITING",
				title: "Integrated Writing Response",
				topics: [],
			},
		},
		items: [
			{
				id: 506574,
				versionId: 1,
				content: {
					parentId: 501998,
					parentType: "SET",
					stem: "Based on the business owner's concerns, provide your preliminary financial assessment. Include potential causes for the discrepancies and outline further investigative steps.",
					choices: [],
					instruction:
						"Watch the video and write your answer in the text box provided.",
					media: null,
				},
				tags: [
					{
						id: 6480,
						type: "Skill",
						label: "Writing",
						breadcrumb: "Writing",
					},
				],
				response: {
					maximumScore: 15,
					subskillsSchema: [
						{
							name: "Clarity and Organization",
							icon: "file-document-outline",
							description: "Structure your response clearly.",
							maxScore: 5,
						},
						{
							name: "Crisis Management",
							icon: "shield-check-outline",
							description: "Effectively address the crisis situation.",
							maxScore: 5,
						},
						{
							name: "Justification",
							icon: "check-circle-outline",
							description: "Provide rationale for your decisions.",
							maxScore: 5,
						},
					],
				},
				metadata: {
					type: "INTEGRATED_WRITING_RESPONSE",
					skill: "WRITING",
					title: "Integrated Writing Response",
					topics: [],
				},
				score: null,
			},
		],
	},
	{
		type: "SET",
		stimulus: {
			id: 501999,
			content: {
				instruction:
					"Watch the video and circle the anomaly visible in the financial statement.",
				passage: {
					title: "Finance Case: Financial Statement Analysis",
					paragraphs: [
						{
							text: "A financial scenario is presented. You see a description of a 35-year-old business owner contacting a telefinance service with concerns about irregularities in his financial statements, discrepancies between cash flow and bank records, and potential fraudulent activities.",
							number: 1,
						},
					],
				},
				media: [
					{
						id: "financeVid001",
						url: "vi6VL8q7XMZm3lJt6ZZ4e00o",
						type: "video",
						transcript:
							"Hi, I'm calling because I've noticed some inconsistencies in my financial statements. In the last quarter, my cash flow appears to have significant discrepancies with the bank records, and there are unusual charges that I cannot account for. I'm concerned that these issues could indicate either a clerical error or potential fraud. Could you help me understand what might be causing these discrepancies and advise on the next steps?",
					},
				],
			},
			metadata: {
				type: "INTEGRATED_MARKING_ANSWER_STIMULUS",
				skill: "READING",
				title: "Integrated Marking Answer Stimulus",
				topics: [],
			},
		},
		items: [
			{
				id: 506575,
				versionId: 1,
				content: {
					parentId: 501999,
					parentType: "SET",
					stem: "Using the provided snapshot of the financial statement, identify and highlight the section where the discrepancies between cash flow and bank records are most evident.",
					choices: [],
					instruction:
						"Watch the video and circle the anomaly visible in the financial statement.",
					media: [
						{
							id: "https://ajxjsnuynuxigljdrsyk.supabase.co/storage/v1/object/public/memorang-assets/demo/acme/financial_statement.png",
							url: "https://ajxjsnuynuxigljdrsyk.supabase.co/storage/v1/object/public/memorang-assets/demo/acme/statement.png",
							type: "image",
						},
					],
				},
				tags: [
					{
						id: 6480,
						type: "Skill",
						label: "Marking",
						breadcrumb: "Marking",
					},
				],
				response: {
					maximumScore: 10,
					subskillsSchema: [
						{
							name: "Accuracy",
							icon: "crosshairs-gps",
							maxScore: 5,
							description:
								"Accurately identify and mark the area of discrepancy.",
						},
						{
							name: "Speed",
							icon: "timer-outline",
							maxScore: 5,
							description:
								"Quickly analyze and annotate without sacrificing accuracy.",
						},
					],
				},
				metadata: {
					type: "MARKING_ANSWER",
					skill: "MARKING",
					title: "Marking Answer",
					topics: [],
				},
				score: null,
			},
		],
	},
	{
		type: "SET",
		stimulus: {
			id: 5019998,
			content: {
				instruction: "Watch the video and record your answer.",
				passage: {
					title: "Finance Case: Financial Statement Analysis",
					paragraphs: [
						{
							text: "A financial scenario is presented. You see a description of a 35-year-old business owner contacting a telefinance service with concerns about irregularities in his financial statements, discrepancies between cash flow and bank records, and potential fraudulent activities.",
							number: 1,
						},
					],
				},
				media: [
					{
						id: "financeVid001",
						url: "vi6VL8q7XMZm3lJt6ZZ4e00o",
						type: "video",
						transcript:
							"Hi, I'm calling because I've noticed some inconsistencies in my financial statements. In the last quarter, my cash flow appears to have significant discrepancies with the bank records, and there are unusual charges that I cannot account for. I'm concerned that these issues could indicate either a clerical error or potential fraud. Could you help me understand what might be causing these discrepancies and advise on the next steps?",
					},
				],
			},
			metadata: {
				type: "LS_STIMULUS",
				skill: "SPEAKING",
				title: "Listen Speak Stimulus",
				topics: ["LS Summary"],
			},
		},
		items: [
			{
				id: 505773434,
				content: {
					parentId: 5019998,
					parentType: "SET",
					stem: "After reviewing the business owner's description and the financial statement, clearly explain your analysis and the recommended steps to address the irregularities. Use clear, non-technical language for better understanding.",
					choices: [],
					instruction: "Watch the video and record your answer.",
					media: null,
				},
				tags: [
					{
						id: 6481,
						type: "Skill",
						label: "Speaking",
						breadcrumb: "Speaking",
					},
				],
				response: {
					maximumScore: 30,
					maximumDuration: 40,
					subskillsSchema: [
						{
							maxScore: 5,
							name: "Communication & Interpersonal Skills",
							icon: "message-outline",
							description: "Demonstrate effective verbal communication.",
						},
						{
							maxScore: 5,
							name: "Responsibility",
							icon: "account-check-outline",
							description: "Show accountability for the incident.",
						},
						{
							maxScore: 5,
							name: "Clarity",
							icon: "microphone-outline",
							description: "Speak clearly and coherently.",
						},
						{
							maxScore: 5,
							name: "Professionalism",
							icon: "briefcase-outline",
							description: "Maintain a professional tone.",
						},
						{
							maxScore: 5,
							name: "Solution Orientation",
							icon: "arrow-right-circle-outline",
							description: "Present clear corrective actions.",
						},
						{
							maxScore: 5,
							name: "Empathy",
							icon: "heart-outline",
							description:
								"Convey understanding and empathy toward stakeholders.",
						},
					],
				},
				metadata: {
					type: "AUDIO_INPUT_EMOTION",
					skill: "SPEAKING",
					title: "Audio Input Item",
					topics: ["LS Summary"],
				},
				score: 0,
			},
		],
	},
];
